/**
 * Verbal Reasoning Test (VRT) — simplified (10 items).
 * Format: short passage + TRUE / FALSE / Cannot Say.
 */

const L = (en, tc, sc) => ({
  en,
  tc: tc != null ? tc : en,
  sc: sc != null ? sc : en
});

const OPT_TFC = [
  { key: 'A', label: L('TRUE', '真', '真') },
  { key: 'B', label: L('FALSE', '假', '假') },
  { key: 'C', label: L('Cannot Say', '無法判斷', '无法判断') }
];

function st3(correctKey) {
  return {
    A: correctKey === 'A' ? 1 : 0,
    B: correctKey === 'B' ? 1 : 0,
    C: correctKey === 'C' ? 1 : 0
  };
}

/**
 * @param {number} n 1-based
 * @param {{en:string,tc:string,sc:string}} passageBody
 */
function passage(n, passageBody) {
  return {
    id: `vrt-p${n}`,
    title: L('Passage', '文章', '文章'),
    body: passageBody
  };
}

/**
 * @param {number} n 1-based
 * @param {{en:string,tc:string,sc:string}} prompt
 * @param {'A'|'B'|'C'} correctKey
 */
function question(n, prompt, correctKey) {
  return {
    id: `vrt-q${n}`,
    displayNumber: n,
    passageId: `vrt-p${n}`,
    prompt,
    options: OPT_TFC,
    scoreTable: st3(correctKey)
  };
}

const PASSAGES = [
  passage(
    1,
    L(
      'Evaluating school clubs has pros and cons. The Student Council prefers a mixed approach. Best practice requires combining numbered surveys (quantitative) and interviews (qualitative). These two methods together give a complete, accurate picture of a club’s success.',
      '評估學校社團有利有弊。學生會偏好混合方法。最佳做法是把量化的問卷（數字調查）與質性的訪談結合；兩者合用才能更完整、準確地反映社團成效。',
      '评估学校社团有利有弊。学生会偏好混合方法。最佳做法是把量化问卷（数字调查）与质性访谈结合；两者合用才能更完整、准确地反映社团成效。'
    )
  ),
  passage(
    2,
    L(
      'School rule changes are either episodic (one-off) or continuous. Continuous change is slow, constantly evolving, and highly popular. This approach treats all students and teachers as equals, adopting ideas based purely on how helpful they are.',
      '校規變更可分為一次性（偶發式）或持續性。持續性變更進展較慢、會不斷演變，而且很受歡迎。此做法把學生與老師視為平等，並只按建議是否有幫助來採納想法。',
      '校规变更可分为一次性（偶发式）或持续性。持续性变更进展较慢、会不断演变，而且很受欢迎。此做法把学生与老师视为平等，并只按建议是否有帮助来采纳想法。'
    )
  ),
  passage(
    3,
    L(
      'Students leading group projects benefit from a holistic strategy. This means focusing on both social teamwork and academic research equally. Experts agree that project groups perform best when social and academic tasks are perfectly balanced.',
      '學生帶領小組專題可受惠於整全策略，即同時兼顧社交協作與學術研究。專家同意：當社交與學術任務能達到完美平衡時，小組表現最佳。',
      '学生带领小组专题可受惠于整全策略，即同时兼顾社交协作与学术研究。专家同意：当社交与学术任务能达到完美平衡时，小组表现最佳。'
    )
  ),
  passage(
    4,
    L(
      'To fix school spirit, the Principal must ask one overarching question. Questions shape how students behave. Because words are powerful, a positively worded question creates longer-lasting enthusiasm and gets students talking about positive changes.',
      '要改善校園精神，校長必須提出一個總體問題。問題會形塑學生的行為。由於措辭具有力量，正向措辭的問題能帶來更持久的熱情，並促使學生談論正面改變。',
      '要改善校园精神，校长必须提出一个总体问题。问题会塑造学生的行为。由于措辞具有力量，正向措辞的问题能带来更持久的热情，并促使学生讨论正面改变。'
    )
  ),
  passage(
    5,
    L(
      'Since the accuracy of sports tryouts varies, coaches must carefully pick testing methods. To build a great team, coaches should combine complementary fitness tests and ensure the tests target the exact skills needed for the sport.',
      '由於體育選拔的準確性有所差異，教練必須慎選測試方法。要建立強隊，教練應結合互補的體能測試，並確保測試能針對該運動所需的關鍵技能。',
      '由于体育选拔的准确性有所差异，教练必须慎选测试方法。要建立强队，教练应结合互补的体能测试，并确保测试能针对该运动所需的关键技能。'
    )
  ),
  passage(
    6,
    L(
      'A school club changes when its members change their daily habits. This means club change is culture change. This aligns with Shared Leadership, where all members act as leaders through daily social teamwork.',
      '當社團成員改變日常習慣時，社團就會改變；換言之，社團變革等於文化變革。這與共享領導相符：所有成員透過日常的社交協作來扮演領導者。',
      '当社团成员改变日常习惯时，社团就会改变；换言之，社团变革等于文化变革。这与共享领导相符：所有成员通过日常的社交协作来扮演领导者。'
    )
  ),
  passage(
    7,
    L(
      'Many tutors think students use AI essay writers too often. However, a third of top-grade students use AI themselves. Recent surveys show that average students are willing to risk failing just to write essays entirely from scratch, more so than top students.',
      '不少導師認為學生太常用 AI 寫作工具。然而，三分之一的高分學生也會使用 AI。近期調查顯示：一般學生更願意冒著不及格的風險也要完全自行撰寫文章，這種傾向比高分學生更強。',
      '不少导师认为学生太常用 AI 写作工具。然而，三分之一的高分学生也会使用 AI。近期调查显示：一般学生更愿意冒着不及格的风险也要完全自行撰写文章，这种倾向比高分学生更强。'
    )
  ),
  passage(
    8,
    L(
      'The official definition of an Ideal Student is complete academic, social, and mental well-being, not just getting high grades. Recently, this was updated to include club participation. Some argue being an ideal student is an ongoing process of adapting, rather than a fixed state.',
      '「理想學生」的官方定義是：具備完整的學業、社交與心理健康，而不只是高分。近期定義更新，加入了社團參與。有些人認為成為理想學生是一個持續適應的過程，而非固定狀態。',
      '“理想学生”的官方定义是：具备完整的学业、社交与心理健康，而不只是高分。近期定义更新，加入了社团参与。有些人认为成为理想学生是一个持续适应的过程，而非固定状态。'
    )
  ),
  passage(
    9,
    // same passage as #8, duplicated per your spec (two questions)
    L(
      'The official definition of an Ideal Student is complete academic, social, and mental well-being, not just getting high grades. Recently, this was updated to include club participation. Some argue being an ideal student is an ongoing process of adapting, rather than a fixed state.',
      '「理想學生」的官方定義是：具備完整的學業、社交與心理健康，而不只是高分。近期定義更新，加入了社團參與。有些人認為成為理想學生是一個持續適應的過程，而非固定狀態。',
      '“理想学生”的官方定义是：具备完整的学业、社交与心理健康，而不只是高分。近期定义更新，加入了社团参与。有些人认为成为理想学生是一个持续适应的过程，而非固定状态。'
    )
  ),
  passage(
    10,
    L(
      'School clubs are acting eco-friendly to look good. Clubs that reduce paper waste save money and boost their popularity. While students do not yet vote for presidents based only on recycling, clubs fear that wasting paper will upset voters.',
      '學校社團採取環保行動有時是為了形象。減少紙張浪費的社團可節省成本並提升人氣。雖然學生尚未只因回收就投票選社長，但社團擔心浪費紙張會惹怒選民。',
      '学校社团采取环保行动有时是为了形象。减少纸张浪费的社团可节省成本并提升人气。虽然学生尚未只因回收就投票选社长，但社团担心浪费纸张会惹怒选民。'
    )
  )
];

const QUESTIONS = [
  question(
    1,
    L(
      'Club evaluation should only use numbered surveys.',
      '社團評估只應使用數字問卷調查。',
      '社团评估只应使用数字问卷调查。'
    ),
    'B'
  ),
  question(
    2,
    L(
      'Students are ignored during one-off rule changes.',
      '在一次性的校規變更中，學生會被忽視。',
      '在一次性的校规变更中，学生会被忽视。'
    ),
    'C'
  ),
  question(
    3,
    L(
      'Non-holistic strategies are completely useless.',
      '非整全策略是完全無用的。',
      '非整全策略是完全无用的。'
    ),
    'C'
  ),
  question(
    4,
    L(
      'Shorter questions guarantee better school spirit.',
      '較短的問題必然帶來更好的校園精神。',
      '较短的问题必然带来更好的校园精神。'
    ),
    'C'
  ),
  question(
    5,
    L(
      'Complementary tests help coaches pick better players.',
      '互補的測試能幫助教練選出更合適的球員。',
      '互补的测试能帮助教练选出更合适的球员。'
    ),
    'A'
  ),
  question(
    6,
    L(
      'Shared Leadership requires social teamwork.',
      '共享領導需要社交協作。',
      '共享领导需要社交协作。'
    ),
    'A'
  ),
  question(
    7,
    L(
      'Top students take fewer risks than average students.',
      '高分學生比一般學生更少冒險。',
      '高分学生比一般学生更少冒险。'
    ),
    'A'
  ),
  question(
    8,
    L(
      'Being an Ideal Student means more than high grades.',
      '成為理想學生不只是要高分。',
      '成为理想学生不只是要高分。'
    ),
    'A'
  ),
  question(
    9,
    L(
      'Everyone agrees high grades guarantee well-being.',
      '所有人都同意高分能保證身心健康。',
      '所有人都同意高分能保证身心健康。'
    ),
    'B'
  ),
  question(
    10,
    L(
      'Clubs ignore what the student voters want.',
      '社團不在意學生選民想要甚麼。',
      '社团不在意学生选民想要什么。'
    ),
    'B'
  )
];

export const verbalReasoningTestPaper = {
  slug: 'verbal-reasoning',
  title: L('Verbal Reasoning', '語言推理', '语言推理'),
  paperInstruction: L(
    'Read each passage. Based only on the information given, decide whether the statement is TRUE, FALSE, or Cannot Say.',
    '閱讀每段文章。只根據所提供的資訊，判斷陳述為「真」、「假」或「無法判斷」。',
    '阅读每段文章。只根据所提供的信息，判断陈述为“真”、“假”或“无法判断”。'
  ),
  timeLimitSeconds: 3600,
  order: 2,
  isPublished: true,
  sections: [
    {
      id: 'vrt-tfc',
      title: L(
        'Truth from text',
        '案例：文本真偽',
        '案例：文本真伪'
      ),
      passages: PASSAGES,
      questions: QUESTIONS
    }
  ]
};

